import { useApolloClient } from "@apollo/client";
import { FormatOptions } from "_graphql-types-frontend/graphql";
import { useMemo } from "react";
import { GET_CASE_STUDY_REPORT, GET_REPORT } from "./graphql";

/** Which background report a task produces, so the right query fetches it. */
export type ReportFile =
  | { kind: "caseStudy" }
  // A template literal rather than the enum: each app's codegen mints its own
  // FormatOptions enum, and either one's values fit this.
  | { kind: "investmentReport"; format: `${FormatOptions}` };

const POLL_INTERVAL_MS = 2000;
// An hour, as the report modal has always allowed.
const MAX_POLLS = 1800;

/**
 * Get a background report's file: the one place the report modal and the chat
 * do it. Both queries answer null while the task runs, a freshly signed link
 * once it is done, and an error when it failed — and refuse a task the caller
 * did not start. Links expire, so ask again for each render; never keep one.
 */
export function useReportFile() {
  const client = useApolloClient();

  return useMemo(() => {
    const fetchUrl = async (
      taskId: string,
      file: ReportFile
    ): Promise<string | null> => {
      if (file.kind === "caseStudy") {
        const { data } = await client.query({
          query: GET_CASE_STUDY_REPORT,
          variables: { id: taskId },
          fetchPolicy: "network-only",
        });
        return data.caseStudyReport ?? null;
      }
      const { data } = await client.query({
        query: GET_REPORT,
        variables: { id: taskId, format: file.format as FormatOptions },
        fetchPolicy: "network-only",
      });
      return data.report ?? null;
    };

    /** Poll until the file is ready; rejects if the task failed or `signal` aborts. */
    const waitForUrl = async (
      taskId: string,
      file: ReportFile,
      signal?: AbortSignal
    ): Promise<string> => {
      for (let i = 0; i < MAX_POLLS; ++i) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        if (signal?.aborted) throw new Error("Stopped waiting for the report");
        const url = await fetchUrl(taskId, file);
        if (url) return url;
      }
      throw new Error("Timed out polling for job");
    };

    return { fetchUrl, waitForUrl };
  }, [client]);
}
